import { Check, Loader2, MoreHorizontal, RefreshCw, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { PROVIDER_ICONS_SMALL, FallbackIcon } from './providerIcons'
import ComposioAppIcon from '@/frontend/components/Integrations/ComposioAppIcon'
import ManagedConnection from '@/frontend/features/ExternalOauth/ManagedConnection'
import { formatLastVerifiedShort, formatVerifiedAbsolute } from '@/frontend/utils/composioRefresh'
import { isComposioIntegration, type BuiltinOrgIntegration, type ComposioOrgIntegration, type OrgIntegration } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  integration: OrgIntegration
  canManage: boolean
  googleCount: number
  metaCount: number
  justConnected: boolean
  refreshing: boolean
  disconnecting: boolean
  busyAction: string | null
  rowRef: (node: HTMLDivElement | null) => void
  onDisconnect: (integration: OrgIntegration) => void
  onRefresh: (integration: ComposioOrgIntegration) => void
  onReconnect: (integration: ComposioOrgIntegration) => void
  onGoogleAccounts: () => void
  onMetaAccounts: () => void
}

export default function WorkerIntegrationRow({
  integration, canManage, googleCount, metaCount, justConnected, refreshing,
  disconnecting, busyAction, rowRef, onDisconnect, onRefresh, onReconnect,
  onGoogleAccounts, onMetaAccounts,
}: Props) {
  if (integration.admin_management) {
    return <ManagedConnection integration={integration} rowRef={rowRef} />
  }

  const builtin = isComposioIntegration(integration) ? null : integration as BuiltinOrgIntegration
  const composio = isComposioIntegration(integration) ? integration : null
  const isGoogle = builtin?.provider === 'google'
  const isMeta = builtin?.provider === 'meta'
  const accountCount = isGoogle ? googleCount : isMeta ? metaCount : 0

  const statusBadge = composio ? (
    composio.status === 'active' ? <Badge variant="success">Ativo</Badge>
      : composio.status === 'error' ? <Badge variant="destructive">Erro</Badge>
      : <Badge variant="secondary">Desconectado</Badge>
  ) : <Badge variant="success">Ativo</Badge>

  return (
    <div
      ref={rowRef}
      className={cn(
        'flex items-center gap-3 py-3 first:pt-0 last:pb-0 transition-colors',
        justConnected && 'animate-message-in',
      )}
    >
      <div className={cn('relative shrink-0 transition-transform', justConnected && 'animate-reveal-bounce')}>
        {composio
          ? <ComposioAppIcon logo={composio.logo} name={composio.provider_label} className="h-5 w-5" />
          : (PROVIDER_ICONS_SMALL[integration.provider] || <FallbackIcon />)}
        {(!composio || composio.status === 'active') && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -right-0.5 -bottom-0.5 flex h-2 w-2"
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-success ring-2 ring-background" />
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {/* Line 1: provider name + (right) status badge + overflow menu */}
        <div className="flex items-center gap-2">
          <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{integration.provider_label}</p>
          {justConnected && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/12 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success ring-1 ring-success/30 animate-message-in">
              <Check className="h-2.5 w-2.5" />
              Conectado
            </span>
          )}
          <div className="shrink-0">{statusBadge}</div>
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Mais ações"
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  variant="destructive"
                  disabled={disconnecting}
                  onSelect={() => onDisconnect(integration)}
                >
                  <Trash2 className="h-4 w-4" />
                  Desconectar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Line 2: status sub-info + actions */}
        {composio?.status === 'error' && (
          <p className="mt-0.5 text-xs text-destructive">Reconecte para voltar a usar</p>
        )}

        <div className="mt-0.5 flex items-center gap-2">
          {composio ? (
            <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-muted-foreground">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate">{formatLastVerifiedShort(composio.last_synced_at)}</span>
                </TooltipTrigger>
                <TooltipContent>{formatVerifiedAbsolute(composio.last_synced_at)}</TooltipContent>
              </Tooltip>
              {canManage && (
                <>
                  <span aria-hidden="true">·</span>
                  <button
                    type="button"
                    disabled={refreshing}
                    onClick={() => onRefresh(composio)}
                    className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground hover:underline underline-offset-2 disabled:opacity-60 disabled:no-underline"
                  >
                    {refreshing
                      ? <><Loader2 className="h-3 w-3 animate-spin" /> verificando…</>
                      : 'atualizar'}
                  </button>
                </>
              )}
            </div>
          ) : isGoogle ? (
            canManage ? (
              <button
                type="button"
                className="min-w-0 flex-1 text-left text-xs text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => onGoogleAccounts()}
              >
                {accountCount > 0 ? `${accountCount} ${accountCount === 1 ? 'conta selecionada' : 'contas selecionadas'}` : 'Selecionar contas Google'}
              </button>
            ) : (
              <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                {accountCount > 0 ? `${accountCount} ${accountCount === 1 ? 'conta selecionada' : 'contas selecionadas'}` : 'Contas Google'}
              </p>
            )
          ) : isMeta ? (
            canManage ? (
              <button
                type="button"
                className="min-w-0 flex-1 text-left text-xs text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => onMetaAccounts()}
              >
                {accountCount > 0 ? `${accountCount} ${accountCount === 1 ? 'conta selecionada' : 'contas selecionadas'}` : 'Selecionar contas Meta'}
              </button>
            ) : (
              <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                {accountCount > 0 ? `${accountCount} ${accountCount === 1 ? 'conta selecionada' : 'contas selecionadas'}` : 'Contas Meta'}
              </p>
            )
          ) : (
            <div className="min-w-0 flex-1" />
          )}

          {canManage && composio && composio.status !== 'active' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => onReconnect(composio)}
              disabled={busyAction === `toolkit:${composio.app_slug}`}
            >
              {busyAction === `toolkit:${composio.app_slug}`
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <RefreshCw className="h-4 w-4" />}
              Reconectar
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
